import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Pin, PinOff, Trash2, Check, CheckCheck,
  Users, User, RefreshCw, ImageIcon, X, Maximize2, Reply, CornerDownRight,
  Eye, EyeOff, ChevronDown, ChevronUp, Smile
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  
  const { user: currentUser, isAdmin } = useAuth();
  
  // New message form
  const [newMessage, setNewMessage] = useState({
    content: '',
    recipient_id: '',
    is_pinned: false
  });
  const [sending, setSending] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { file, preview }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Reply state
  const [replyingTo, setReplyingTo] = useState(null); // The message being replied to
  
  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef(null);
  
  // Common emoji list
  const EMOJI_LIST = [
    // Smileys
    '😊', '😃', '😄', '😁', '😅', '😂', '🤣', '😉', '😍', '🥰',
    '😘', '😎', '🤔', '🤗', '😏', '😮', '😲', '😳', '😢', '😭',
    // Gestures
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '💪', '🤝', '✋',
    // Objects & symbols
    '❤️', '💯', '⭐', '🔥', '✅', '❌', '⚠️', '📌', '🚴', '🔧',
    // Work related
    '📦', '📝', '📋', '📊', '💼', '🛠️', '⚙️', '🔔', '💡', '🎯'
  ];

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = newMessage.content;
      const newText = text.substring(0, start) + emoji + text.substring(end);
      setNewMessage({ ...newMessage, content: newText });
      // Set cursor position after emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setNewMessage({ ...newMessage, content: newMessage.content + emoji });
    }
    setShowEmojiPicker(false);
  };

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const messageType = activeTab === 'all' ? '' : activeTab;
      const params = messageType ? `?message_type=${messageType}` : '';
      const res = await axios.get(`${API}/messages${params}`);
      setMessages(res.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/messages/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, [fetchMessages]);

  const markAsRead = async (messageId) => {
    try {
      await axios.post(`${API}/messages/${messageId}/read`);
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, is_read_by: [...msg.is_read_by, currentUser.id] }
          : msg
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const messageType = activeTab === 'all' ? '' : activeTab;
      const params = messageType ? `?message_type=${messageType}` : '';
      await axios.post(`${API}/messages/read-all${params}`);
      fetchMessages();
      toast.success('Todos los mensajes marcados como leídos');
    } catch {
      toast.error('Error al marcar como leídos');
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10 MB');
      return;
    }
    const preview = URL.createObjectURL(file);
    setPendingImage({ file, preview });
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const removePendingImage = () => {
    if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
    setPendingImage(null);
  };

  // Send message (with optional image)
  const handleSendMessage = async () => {
    if (!newMessage.content.trim() && !pendingImage) {
      toast.error('Escribe un mensaje o adjunta una imagen');
      return;
    }
    if (activeTab === 'personal' && !newMessage.recipient_id && !replyingTo) {
      toast.error('Selecciona un destinatario');
      return;
    }

    setSending(true);
    try {
      let imageUrl = null;

      // Upload image first if present
      if (pendingImage) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', pendingImage.file);
        const uploadRes = await axios.post(`${API}/messages/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.image_url;
        setUploading(false);
      }

      // Determine recipient: use replyingTo's sender for personal replies, or form value
      let recipientId = null;
      if (replyingTo) {
        // If replying to a personal message, reply to the sender
        if (replyingTo.message_type === 'personal') {
          recipientId = replyingTo.sender_id !== currentUser.id 
            ? replyingTo.sender_id 
            : replyingTo.recipient_id;
        }
        // If replying to a general message, keep it general (no recipient)
      } else if (activeTab === 'personal') {
        recipientId = newMessage.recipient_id;
      }

      const data = {
        content: newMessage.content,
        recipient_id: recipientId,
        is_pinned: newMessage.is_pinned,
        image_url: imageUrl,
        reply_to_id: replyingTo?.id || null
      };
      
      await axios.post(`${API}/messages`, data);
      toast.success('Mensaje enviado');
      setNewMessage({ content: '', recipient_id: '', is_pinned: false });
      setReplyingTo(null);
      removePendingImage();
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    // Focus on textarea
    setTimeout(() => {
      const textarea = document.querySelector('[data-testid="message-input"]');
      if (textarea) textarea.focus();
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const togglePin = async (message) => {
    try {
      await axios.put(`${API}/messages/${message.id}`, {
        is_pinned: !message.is_pinned
      });
      toast.success(message.is_pinned ? 'Mensaje desfijado' : 'Mensaje fijado');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fijar mensaje');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success('Mensaje eliminado');
      setDeleteConfirm(null);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteConfirm(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const isUnread = (message) => !message.is_read_by.includes(currentUser.id);

  const getUnreadCount = (type) => {
    return messages.filter(m => {
      if (type === 'general') return m.message_type === 'general' && isUnread(m);
      if (type === 'personal') return m.message_type === 'personal' && isUnread(m);
      return isUnread(m);
    }).length;
  };

  const filteredMessages = messages.filter(m => {
    if (activeTab === 'general') return m.message_type === 'general';
    if (activeTab === 'personal') return m.message_type === 'personal';
    return true;
  });

  // Resolve image src with backend URL prefix
  const getImageSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // MessageCard component
  const MessageCard = ({ message }) => {
    const unread = isUnread(message);
    const canDelete = message.sender_id === currentUser.id || message.recipient_id === currentUser.id || isAdmin;
    const isOwnMessage = message.sender_id === currentUser.id;
    const [showReadStatus, setShowReadStatus] = useState(false);

    useEffect(() => {
      if (unread) markAsRead(message.id);
    }, []);

    const imgSrc = getImageSrc(message.image_url);

    // For personal messages: check if recipient has read it
    const isPersonalReadByRecipient = () => {
      if (message.message_type !== 'personal') return false;
      if (!isOwnMessage) return true; // Show as read if we're the recipient viewing it
      const recipientId = message.recipient_id;
      return message.is_read_by?.includes(recipientId);
    };

    // Read status indicator for personal messages
    const PersonalReadIndicator = () => {
      if (message.message_type !== 'personal') return null;
      const read = isPersonalReadByRecipient();
      
      return (
        <span className={`inline-flex items-center ${read ? 'text-blue-500' : 'text-slate-400'}`} title={read ? 'Leído' : 'Enviado'}>
          {read ? <CheckCheck className="w-4 h-4" /> : <Check className="w-4 h-4" />}
        </span>
      );
    };

    // Read status for general messages
    const GeneralReadStatus = () => {
      if (message.message_type !== 'general') return null;
      
      const readCount = message.read_by_users?.length || 0;
      const unreadCount = message.unread_by_users?.length || 0;
      const totalCount = readCount + unreadCount;
      
      return (
        <Popover open={showReadStatus} onOpenChange={setShowReadStatus}>
          <PopoverTrigger asChild>
            <button 
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              data-testid={`read-status-btn-${message.id}`}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>{readCount}/{totalCount}</span>
              {showReadStatus ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="end">
            <div className="p-3 border-b bg-slate-50">
              <h4 className="font-semibold text-sm text-slate-700">Estado de lectura</h4>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {/* Users who have read */}
              {message.read_by_users?.length > 0 && (
                <div className="p-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-green-600 mb-1.5">
                    <CheckCheck className="w-3.5 h-3.5" />
                    Leído por ({readCount})
                  </div>
                  <div className="space-y-1">
                    {message.read_by_users.map(user => (
                      <div key={user.id} className="text-xs text-slate-600 pl-5">
                        {user.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Users who haven't read */}
              {message.unread_by_users?.length > 0 && (
                <div className="p-2 border-t">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1.5">
                    <EyeOff className="w-3.5 h-3.5" />
                    Sin leer ({unreadCount})
                  </div>
                  <div className="space-y-1">
                    {message.unread_by_users.map(user => (
                      <div key={user.id} className="text-xs text-slate-400 pl-5">
                        {user.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      );
    };

    return (
      <div 
        className={`p-4 rounded-lg border transition-all ${
          message.is_pinned 
            ? 'bg-amber-50 border-amber-300 shadow-sm' 
            : unread 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-white border-slate-200 hover:border-slate-300'
        }`}
        data-testid={`message-${message.id}`}
      >
        {/* Reply reference - compact short reference only */}
        {message.reply_to && (
          <div className="mb-2 flex items-center gap-2 px-2 py-1.5 bg-slate-100 rounded border-l-2 border-slate-400 text-xs">
            <Reply className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="text-slate-500 truncate">
              <span className="font-medium">{message.reply_to.sender_name}:</span>{' '}
              {message.reply_to.content || '[Imagen]'}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            {message.is_pinned && <Pin className="w-4 h-4 text-amber-600 flex-shrink-0" />}
            <span className={`font-semibold text-sm ${isOwnMessage ? 'text-green-700' : 'text-slate-800'}`}>
              {isOwnMessage ? 'Tú' : message.sender_name}
            </span>
            {message.message_type === 'personal' && (
              <span className="text-xs text-slate-500">
                {isOwnMessage 
                  ? `→ ${message.recipient_name}` 
                  : message.recipient_id === currentUser.id ? '→ Para ti' : ''}
              </span>
            )}
            <span className="text-xs text-slate-400">{formatDate(message.created_at)}</span>
            
            {/* Read indicators */}
            {message.message_type === 'personal' && isOwnMessage && <PersonalReadIndicator />}
            {message.message_type === 'general' && <GeneralReadStatus />}
          </div>
          
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Reply button */}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => handleReply(message)}
              title="Responder"
              data-testid={`reply-btn-${message.id}`}>
              <Reply className="w-4 h-4 text-slate-400 hover:text-blue-600" />
            </Button>
            
            {isAdmin && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => togglePin(message)}
                title={message.is_pinned ? 'Desfijar' : 'Fijar'}>
                {message.is_pinned 
                  ? <PinOff className="w-4 h-4 text-amber-600" />
                  : <Pin className="w-4 h-4 text-slate-400 hover:text-amber-600" />}
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => setDeleteConfirm(message)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Content */}
        {message.content && (
          <p className="text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
            {message.content}
          </p>
        )}

        {/* Attached image */}
        {imgSrc && (
          <div className="mt-2 relative group cursor-pointer inline-block" 
            onClick={() => setLightboxUrl(imgSrc)} data-testid={`message-image-${message.id}`}>
            <img 
              src={imgSrc} alt="Adjunto" 
              className="max-w-xs max-h-48 rounded-lg border border-slate-200 object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
              <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-orange-500" />
            Mensajes
          </h1>
          <p className="text-slate-500 text-sm">Comunicación interna del equipo</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={markAllAsRead} data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-2" />
            Marcar todo leído
          </Button>
          <Button variant="outline" size="sm" onClick={fetchMessages} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="general" className="flex items-center gap-2" data-testid="tab-general">
            <Users className="w-4 h-4" />
            General
            {getUnreadCount('general') > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                {getUnreadCount('general')}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="personal" className="flex items-center gap-2" data-testid="tab-personal">
            <User className="w-4 h-4" />
            Personal
            {getUnreadCount('personal') > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                {getUnreadCount('personal')}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* New Message Form */}
        <Card className="mt-4">
          <CardContent className="pt-4">
            <div className="space-y-3">
              {/* Replying to indicator - compact */}
              {replyingTo && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200" data-testid="replying-to-indicator">
                  <Reply className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-sm text-blue-700">
                      Respondiendo a <span className="font-medium">{replyingTo.sender_id === currentUser.id ? 'ti mismo' : replyingTo.sender_name}</span>:
                    </span>
                    <span className="text-xs text-blue-500 truncate max-w-[200px]">
                      "{replyingTo.content?.substring(0, 30) || '[Imagen]'}{replyingTo.content?.length > 30 ? '...' : ''}"
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                    onClick={cancelReply} data-testid="cancel-reply-btn">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Recipient selector (only for personal tab and not replying) */}
              {activeTab === 'personal' && !replyingTo && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-700 w-16">Para:</label>
                  <Select 
                    value={newMessage.recipient_id} 
                    onValueChange={(v) => setNewMessage({ ...newMessage, recipient_id: v })}
                  >
                    <SelectTrigger className="flex-1" data-testid="recipient-select">
                      <SelectValue placeholder="Selecciona destinatario" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Message input */}
              <Textarea
                ref={textareaRef}
                placeholder={activeTab === 'general' 
                  ? "Escribe un mensaje para todo el equipo..." 
                  : "Escribe un mensaje personal..."
                }
                value={newMessage.content}
                onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                rows={3}
                className="resize-none"
                data-testid="message-input"
              />

              {/* Pending image preview */}
              {pendingImage && (
                <div className="relative inline-block" data-testid="pending-image-preview">
                  <img src={pendingImage.preview} alt="Vista previa"
                    className="max-h-32 rounded-lg border border-slate-200 object-cover" />
                  <button onClick={removePendingImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md hover:bg-red-600"
                    data-testid="remove-pending-image">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Image upload button */}
                  <input type="file" accept="image/*" ref={fileInputRef}
                    onChange={handleImageSelect} className="hidden" data-testid="image-file-input" />
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-slate-500 hover:text-orange-600"
                    title="Adjuntar imagen"
                    data-testid="attach-image-btn">
                    <ImageIcon className="w-5 h-5" />
                  </Button>
                  
                  {/* Emoji picker button */}
                  <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="ghost" size="sm"
                        className="text-slate-500 hover:text-orange-600"
                        title="Añadir emoji"
                        data-testid="emoji-picker-btn">
                        <Smile className="w-5 h-5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="start">
                      <div className="mb-2">
                        <h4 className="text-sm font-medium text-slate-700 mb-1">Emojis</h4>
                      </div>
                      <div className="grid grid-cols-10 gap-1">
                        {EMOJI_LIST.map((emoji, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => insertEmoji(emoji)}
                            className="w-7 h-7 flex items-center justify-center text-lg hover:bg-slate-100 rounded cursor-pointer transition-colors"
                            data-testid={`emoji-btn-${idx}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {isAdmin && (
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newMessage.is_pinned}
                        onChange={(e) => setNewMessage({ ...newMessage, is_pinned: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      <Pin className="w-4 h-4" />
                      Fijar
                    </label>
                  )}
                </div>
                <Button 
                  onClick={handleSendMessage} 
                  disabled={sending || uploading || (!newMessage.content.trim() && !pendingImage)}
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {uploading ? 'Subiendo...' : sending ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <TabsContent value="general" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
          ) : filteredMessages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay mensajes generales</p>
                <p className="text-sm">Escribe el primer mensaje para el equipo</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map(message => (
                <MessageCard key={message.id} message={message} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="personal" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner w-8 h-8" /></div>
          ) : filteredMessages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay mensajes personales</p>
                <p className="text-sm">Envía un mensaje a un compañero</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map(message => (
                <MessageCard key={message.id} message={message} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Image Lightbox */}
      <Dialog open={!!lightboxUrl} onOpenChange={() => setLightboxUrl(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-2 bg-black/95 border-none" data-testid="image-lightbox">
          <span className="sr-only">Imagen ampliada</span>
          {lightboxUrl && (
            <img src={lightboxUrl} alt="Imagen ampliada"
              className="max-w-full max-h-[85vh] object-contain mx-auto rounded"
              onClick={() => setLightboxUrl(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar mensaje?</AlertDialogTitle>
            <AlertDialogDescription>
              El mensaje se eliminará de tu vista. La otra persona seguirá viéndolo hasta que también lo elimine.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm?.id)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
